import { Button, Form, Modal, ModalBody, ModalHeader, Table } from 'react-bootstrap';
import { useContext, useEffect, useState } from 'react';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { MarketSignalAlarmConfig } from '@/modules/market-signals/model/market-signals/market-signal-alarm-config';
import { Page } from '@/shared/pagination/export';
import MarketSignalAlarmEditModal from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-edit-modal';
import MarketSignalAlarmType from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  MarketSignalContext,
  marketSignalSounds,
} from '@/modules/market-signals/contexts/market-signal-context';
import { permissionToGroupName } from '@/modules/market-signals/components/market-signals/alarms/permissions-to-group-name';

export default function MarketSignalAlarmConfigModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const [alarms, setAlarms] = useState<Page<MarketSignalAlarmConfig>>();
  const [selectedAlarm, setSelectedAlarm] = useState<MarketSignalAlarmConfig>();
  const { streams } = useContext(MarketSignalContext);

  const refresh = () => marketSignalApi.getAlarmConfigs(0).then(setAlarms);

  useEffect(() => {
    refresh();
  }, [show]);

  const enableAlarm = (a: MarketSignalAlarmConfig, b: boolean) => {
    if (!a._id) return;

    marketSignalApi.toggleAlarmEnable(a._id, b).then(value => {
      refresh();
    });
  };

  const newAlarm = () => {
    setSelectedAlarm({
      soundType: 'beep',
      type: 'llm',
      temperature: 0.1,
      condition: '',
      enabled: true,
      streams: [],
    });
  };

  return (
    <Modal
      show={show}
      size={'lg'}
      fullscreen={'lg-down'}
      onBackdropClick={onClose}
      onHide={onClose}
    >
      <ModalHeader>
        <div className={'d-flex w-100 justify-content-between align-items-center'}>
          <div className={'h3'}>Market Signal Alarm Config</div>
          <Button onClick={onClose} variant={'warning'}>
            Close
          </Button>
        </div>
      </ModalHeader>

      <ModalBody>
        <Table>
          <thead>
            <tr>
              <th className="text-center">Type</th>
              <th>Condition</th>
              <th>Presentation</th>
              <th></th>
              <th className="text-center">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {alarms?.content
              .sort((a, b) => ((a._id as string) > (b._id as string) ? 1 : -1))
              .map(value => (
                <tr key={value._id}>
                  <td className="text-center">
                    <MarketSignalAlarmType alarm={value} />
                  </td>
                  <td>
                    <div>
                      <span>
                        {value.type === 'llm' && 'Will send an alarm if: '}
                        {value.type === 'string' && 'Will search the text for: '}
                      </span>
                      <code className={'fw-bold ' + (value.type === 'llm' ? 'text-info' : '')}>
                        {value.condition}
                      </code>
                    </div>

                    {value.type === 'llm' && (
                      <span className={'small'}>
                        Temperature:{' '}
                        <code className={'fw-bold text-info'}>{value.temperature}</code>
                      </span>
                    )}

                    {value.type === 'string' && (
                      <span className={'small'}>
                        Match Mode:{' '}
                        <code className={'fw-bold'}>
                          {value.doOrSeparator ? 'match ANY term' : 'match ALL terms'}
                        </code>
                      </span>
                    )}

                    <div className={'small text-muted'}>
                      {value.streams
                        ?.map(value => streams.find(s => s.code === value)?.name)
                        .join(', ')}
                      {(!value.streams || value.streams.length === 0) && 'All Streams'}
                    </div>
                  </td>
                  <td className={'small text-nowrap'}>
                    <div>
                      {marketSignalSounds.find(value1 => value1.file === value.soundType)?.name ||
                        '???'}
                    </div>

                    <div>{!value.showDesktopNotif ? 'No d' : 'D'}esktop alerts</div>

                    <div className={'text-muted'}>
                      {value.owner?.type === 'group' &&
                        `[${permissionToGroupName(value.owner.ownerId)}]`}
                      {value.owner?.type === 'group' || 'Only Me'}
                    </div>
                  </td>
                  <td className="text-center">
                    <Button
                      size={'sm'}
                      variant={'outline-secondary'}
                      onClick={() => setSelectedAlarm(value)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                  </td>

                  <td className="text-center">
                    <Form.Check // prettier-ignore
                      type="switch"
                      id="custom-switch"
                      onChange={event => enableAlarm(value, event.target.checked)}
                      checked={value.enabled}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>

        <div>
          <Button variant={'secondary'} onClick={newAlarm}>
            <FontAwesomeIcon icon={faPlus} /> New Alarm
          </Button>
        </div>

        <MarketSignalAlarmEditModal
          alarm={selectedAlarm}
          onClose={() => {
            setSelectedAlarm(undefined);
            refresh();
          }}
        />
      </ModalBody>
    </Modal>
  );
}
